import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { TradeBrandLogo } from './TradeBrandLogo'
import { TradeThemeToggle } from './TradeThemeToggle'
import { MetaTraderMark } from './MetaTraderMark'
import { SeoHead } from '../seo/SeoHead'
import { TRADE_LANDING_PAGE, type PageSeo } from '../seo/pages'
import { HERO_RISK_NOTICE, RISK_LINKS } from '../risk-copy'
import { portalLinks } from '../portal-links'
import {
  trackLandingView,
  trackRiskDisclosureClicked,
  trackSignInClicked,
} from '../../analytics/track'
import { TradeCheckEmailCard, TradeLandingRegisterForm } from './TradeLandingRegisterForm'
import { TradeExplainer } from './TradeExplainer'
import { TradeKpiPreview } from './TradeKpiPreview'
import { TRADE_LANDING_FOOTER_RISK, isTradeLandingHost, tradeLandingOrigin } from './trade-landing-config'
import '../web-test-hero.css'
import './trade-landing.css'
import './trade-landing-mobile.css'

const NAV = [
  { label: 'How It Works', href: '#how-it-works' },
  { label: 'Watch Video', href: '#watch-video' },
  { label: 'Risk Disclosure', href: '#risk-disclosure' },
] as const

function tradeJsonLd() {
  const url = `${tradeLandingOrigin()}/`
  return [
    {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: TRADE_LANDING_PAGE.title,
      description: TRADE_LANDING_PAGE.description,
      url,
      inLanguage: 'en',
      isPartOf: { '@type': 'WebSite', name: 'Perfect Predict', url },
    },
  ]
}

export function TradeLandingPage() {
  const [menuOpen, setMenuOpen] = useState(false)
  const [registered, setRegistered] = useState<{ maskedEmail: string; devToken: string } | null>(
    null,
  )
  const logoHref = isTradeLandingHost() ? '/' : '/go/trade'
  const seoPage = {
    ...TRADE_LANDING_PAGE,
    canonicalUrl: `${tradeLandingOrigin()}/`,
    robots: (isTradeLandingHost() ? 'index, follow' : 'noindex, follow') as PageSeo['robots'],
  }

  useEffect(() => {
    trackLandingView()
  }, [])

  useEffect(() => {
    if (!registered) return
    window.scrollTo({ top: 0, behavior: 'auto' })
    document.getElementById('tl-reg-title')?.focus()
  }, [registered])

  function goToRegister() {
    document.getElementById('create-account')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    const first = document.getElementById('trade-first-name') as HTMLInputElement | null
    first?.focus()
  }

  return (
    <div className={`wt-page tl-page${registered ? ' tl-page--check-email' : ''}`}>
      <SeoHead path="/go/trade" page={seoPage} jsonLd={tradeJsonLd()} />
      <a className="tl-skip" href="#create-account">
        Skip to Create Account
      </a>

      <header className="tl-header">
        <div className="tl-header__inner">
          <TradeBrandLogo href={logoHref} />

          <nav className="tl-nav" aria-label="Primary" hidden={Boolean(registered)}>
            {NAV.map((item) => (
              <a
                key={item.href}
                href={item.href}
                onClick={() => {
                  if (item.href === '#risk-disclosure') trackRiskDisclosureClicked('nav')
                }}
              >
                {item.label}
              </a>
            ))}
          </nav>

          <div className="tl-header__end">
            <div className="wt-header__actions tl-header__actions">
              <a
                className="wt-btn wt-btn--ghost"
                href={portalLinks.login}
                onClick={() => trackSignInClicked('header')}
              >
                Sign In
              </a>
              {registered ? null : (
                <button type="button" className="wt-btn wt-btn--primary" onClick={goToRegister}>
                  Create Account
                </button>
              )}
            </div>
            <TradeThemeToggle />
            {registered ? null : (
            <button
              type="button"
              className="wt-menu-toggle"
              aria-label={menuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((v) => !v)}
            >
              <span className="wt-menu-toggle__bars" aria-hidden="true">
                <span />
                <span />
                <span />
              </span>
            </button>
            )}
          </div>
        </div>

        {registered ? null : (
        <div className="wt-mobile-menu tl-mobile-menu" hidden={!menuOpen}>
          <nav aria-label="Mobile">
            {NAV.map((item) => (
              <a
                key={item.href}
                href={item.href}
                onClick={() => {
                  setMenuOpen(false)
                  if (item.href === '#risk-disclosure') trackRiskDisclosureClicked('nav')
                }}
              >
                {item.label}
              </a>
            ))}
          </nav>
          <div className="wt-mobile-menu__actions">
            <a
              className="wt-btn wt-btn--ghost"
              href={portalLinks.login}
              onClick={() => {
                setMenuOpen(false)
                trackSignInClicked('header')
              }}
            >
              Sign In
            </a>
            <button
              type="button"
              className="wt-btn wt-btn--primary"
              onClick={() => {
                setMenuOpen(false)
                goToRegister()
              }}
            >
              Create Account
            </button>
          </div>
        </div>
        )}
      </header>

      <main>
        {registered ? (
          <section className="tl-check" aria-labelledby="tl-reg-title">
            <TradeCheckEmailCard
              maskedEmail={registered.maskedEmail}
              devToken={registered.devToken}
            />
          </section>
        ) : (
          <>
        <div className="tl-stack">
        <section className="tl-hero" aria-labelledby="tl-hero-title">
          <div className="tl-hero__grid">
            <div className="tl-hero__left">
              <div className="tl-hero__copy">
                <p className="wt-eyebrow">Structured Prediction Trading</p>
                <h1 id="tl-hero-title" className="tl-hero__title">
                  <span className="tl-hero__title-d">
                    Directional Trading with <span className="tl-hero__accent">Defined Targets.</span>
                  </span>
                  <span className="tl-hero__title-m">
                    <span className="tl-hero__title-m-a">Choose a Plan.</span>
                    <span className="tl-hero__accent">Predict the Direction.</span>
                  </span>
                </h1>
                <p className="wt-body tl-hero__lead-d">
                  Choose a strategy. Select BUY or SELL. Know your target, stop and potential return
                  before you trade.
                </p>
                <p className="wt-body tl-hero__lead-m">
                  Perfect Predict structures the target and stop.
                  You choose BUY or SELL.
                </p>
                <button type="button" className="wt-btn wt-btn--primary tl-m-cta" onClick={goToRegister}>
                  Create Account
                </button>
                <p className="wt-hero-risk">{HERO_RISK_NOTICE}</p>
                <a className="tl-hero__watch" href="#watch-video">
                  <span className="tl-hero__watch-icon" aria-hidden="true" />
                  <span className="tl-hero__watch-d">Watch explainer</span>
                  <span className="tl-hero__watch-m">Watch How It Works</span>
                </a>
              </div>
              <TradeKpiPreview />
            </div>
            <TradeLandingRegisterForm onRegistered={setRegistered} />
          </div>
        </section>

        <section id="how-it-works" className="tl-how" aria-labelledby="tl-how-title">
          <div className="tl-inner">
            <header className="tl-section-head">
              <h2 id="tl-how-title">How Perfect Predict Works</h2>
            </header>
            <div className="tl-how__split">
              <ol className="tl-how__grid">
                <li>
                  <article className="tl-card">
                    <div className="tl-card__top">
                      <span className="tl-card__step">01</span>
                      <div className="tl-card__visual" aria-hidden="true">
                        <span>SAFE</span>
                        <span className="is-on">BALANCE</span>
                        <span>AGGRESSIVE</span>
                      </div>
                    </div>
                    <h3>Choose Strategy</h3>
                    <p className="tl-card__label">SAFE / BALANCE / AGGRESSIVE</p>
                    <p>Select a structured prediction strategy that fits your market outlook.</p>
                    <span className="tl-card__chevron" aria-hidden="true" />
                  </article>
                </li>
                <li>
                  <article className="tl-card">
                    <div className="tl-card__top">
                      <span className="tl-card__step">02</span>
                      <div className="tl-card__visual" aria-hidden="true">
                        <span className="tl-dir tl-dir--buy">BUY</span>
                        <span className="tl-dir tl-dir--sell">SELL</span>
                      </div>
                    </div>
                    <h3>Choose Direction</h3>
                    <p className="tl-card__label">
                      <span className="tl-dir tl-dir--buy">BUY</span>
                      {' / '}
                      <span className="tl-dir tl-dir--sell">SELL</span>
                    </p>
                    <p>You choose the direction. Perfect Predict does not choose for you.</p>
                    <span className="tl-card__chevron" aria-hidden="true" />
                  </article>
                </li>
                <li>
                  <article className="tl-card">
                    <div className="tl-card__top">
                      <span className="tl-card__step">03</span>
                      <MetaTraderMark className="tl-card__mt5" />
                    </div>
                    <h3>Execute on MetaTrader 5</h3>
                    <p>Your selected BUY or SELL trade is executed through MetaTrader 5 infrastructure.</p>
                    <span className="tl-card__chevron" aria-hidden="true" />
                  </article>
                </li>
              </ol>
              <TradeExplainer />
            </div>

            <section className="tl-m-process tl-m-only" aria-labelledby="tl-m-process-title">
              <h2 id="tl-m-process-title">
                <span>You decide the direction.</span>
                <span className="tl-m-process__accent">Perfect Predict defines the structure.</span>
              </h2>
              <ol className="tl-m-steps">
                <li>
                  <span className="tl-m-steps__num" aria-hidden="true">01</span>
                  <div>
                    <h3>Choose Strategy</h3>
                    <p>
                      SAFE / <em>BALANCE</em> / AGGRESSIVE
                    </p>
                  </div>
                </li>
                <li>
                  <span className="tl-m-steps__num" aria-hidden="true">02</span>
                  <div>
                    <h3>Choose Direction</h3>
                    <p>
                      <span className="tl-dir tl-dir--buy">BUY</span>
                      {' or '}
                      <span className="tl-dir tl-dir--sell">SELL</span>
                    </p>
                    <p className="tl-m-steps__note">Perfect Predict does not choose for you.</p>
                  </div>
                </li>
                <li>
                  <span className="tl-m-steps__num" aria-hidden="true">03</span>
                  <div>
                    <h3>Review Before Trading</h3>
                    <p>Target / Stop / Potential</p>
                  </div>
                </li>
                <li>
                  <span className="tl-m-steps__num" aria-hidden="true">04</span>
                  <div>
                    <h3>Execute</h3>
                    <p>MetaTrader 5</p>
                  </div>
                </li>
              </ol>
            </section>
          </div>
        </section>

        <section className="tl-m-diff tl-m-only" aria-labelledby="tl-m-diff-title">
          <h2 id="tl-m-diff-title">What makes it different?</h2>
          <ul className="tl-m-diff__grid">
            <li>
              <span className="tl-m-diff__icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="8" stroke="currentColor" strokeWidth="1.6" />
                  <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.6" />
                  <path d="M12 2v3M12 19v3M2 12h3M19 12h3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                </svg>
              </span>
              <h3>Strategy-led</h3>
              <p>Choose Safe, Balance or Aggressive</p>
            </li>
            <li>
              <span className="tl-m-diff__icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none">
                  <path d="M8 11V5l-3 3M8 5l3 3" stroke="#0159FB" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M16 13v6l3-3M16 19l-3-3" stroke="#FF3D00" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </span>
              <h3>Direction is yours</h3>
              <p>
                Select <span className="tl-dir tl-dir--buy">BUY</span> or{' '}
                <span className="tl-dir tl-dir--sell">SELL</span>
              </p>
            </li>
            <li>
              <span className="tl-m-diff__icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none">
                  <path
                    d="M12 3 5 6.5v5.2c0 4.2 2.9 7.4 7 8.8 4.1-1.4 7-4.6 7-8.8V6.5L12 3Z"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
              <h3>Defined before trade</h3>
              <p>Review target and stop</p>
            </li>
          </ul>
        </section>
        </div>
          </>
        )}
      </main>

      <footer className="tl-footer">
        <p className="tl-operator" aria-label="Operator disclosure">
          Operated by InfinityTech
        </p>
        <div className="tl-inner tl-footer__inner">
          <div className="tl-footer__brand">
            <TradeBrandLogo href={logoHref} />
            <p className="tl-footer__age" aria-label="Age restriction 18 plus">
              18+
            </p>
          </div>
          <nav className="tl-footer__nav" aria-label="Legal">
            <Link to={RISK_LINKS.privacy}>Privacy Policy</Link>
            <Link to={RISK_LINKS.terms}>Terms &amp; Conditions</Link>
            <Link
              to={RISK_LINKS.disclosure}
              onClick={() => trackRiskDisclosureClicked('footer')}
            >
              Risk Disclosure
            </Link>
            <Link to="/contact">Contact</Link>
          </nav>
        </div>
        <div id="risk-disclosure" className="tl-inner tl-footer__risk">
          <p>{TRADE_LANDING_FOOTER_RISK}</p>
        </div>
      </footer>
    </div>
  )
}
